import { INotificationPayload, NavigatorMessage } from "@cruisen/contract"
import { getToken, onMessage } from "firebase/messaging"
import { auth, fcmService } from "./firebase.ts"
import Config from "../config"
import { saveUserDeviceToken } from "./firebase-functions/userDeviceTokens.ts"
import { _rootStore } from "../models/helpers/useStores.ts"
import { getTrip } from "./firestore/trips.ts"

const serviceWorkerMessageListener = (event) => {
  if (!event.data.action) {
    return
  }
  switch (event.data.action) {
    case "handle-notification-click":
      handleNotificationClick(event.data.notification?.data?.tripId).catch((error) =>
        console.error("notification click handle failure", {
          error,
          notification: event.data.notification,
        }),
      )
      break
    case "background-notification":
      incrementUnreadMessagesCount(event.data.notification)
      break
    // no default
  }
}

async function requestPermission() {
  const permission = await Notification.requestPermission()
  if (permission === "granted") {
    // Generate Device Token for notification
    const fcmInstance = await fcmService
    if (!fcmInstance) return
    const token = await getToken(fcmInstance, {
      vapidKey: Config.firebaseVapidKey,
    })
    console.log("Token Gen", token)
    if (token) await saveUserDeviceToken({ token })
  } else if (permission === "denied") {
    console.log("Denied for the notification")
  }
}

function incrementUnreadMessagesCount(notification: INotificationPayload<Record<string, unknown>>) {
  const notificationTripId = notification.data?.tripId as string

  if (typeof notificationTripId !== "string" || !notificationTripId) return

  const currentNavigatorState = _rootStore.navigatorStates.get(notificationTripId)

  if (
    notification.timestamp &&
    (!currentNavigatorState ||
      (currentNavigatorState.lastMessageDate &&
        notification.timestamp > currentNavigatorState.lastMessageDate))
  ) {
    const navigatorStates = { ..._rootStore.navigatorStates.toJSON() }

    navigatorStates[notificationTripId] = {
      readMessageIndex: currentNavigatorState?.readMessageIndex ?? 0,
      lastMessageIndex: (currentNavigatorState?.lastMessageIndex ?? 0) + 1,
      lastMessageDate: notification.timestamp ? notification.timestamp : Date.now(),
    }

    _rootStore.setProp("navigatorStates", navigatorStates)
    console.log("increment trip unread messages count", notificationTripId)
  }
}

export function initializeNotifications(): () => void {
  const timeout = setTimeout(() => {
    requestPermission().catch((err) => console.error("Error in requestPermission", err))
  }, 1000)

  let unsubscribeOnMessageEvent = () => {}

  fcmService.then((fcmInstance) => {
    if (!fcmInstance) return

    unsubscribeOnMessageEvent = onMessage(fcmInstance, (payload) => {
      try {
        const notification: INotificationPayload<Record<string, unknown>> = payload.data
          .notification
          ? JSON.parse(payload.data.notification)
          : undefined
        console.log("firebase cloud message received", { payload, notification })

        if (notification.eventType === "chatMessage") {
          const tripId = notification.data.tripId as string
          const navigatorMessage = {
            id: notification.id,
            message: notification.body,
            index: notification.data.index,
            timestamp: notification.timestamp,
            source: "user",
            userId: notification.data.userId,
            userFirstName: notification.data.userFirstName,
            userLastName: notification.data.userLastName,
            userAvatar: "",
            type: "userMessage",
            file: null,
          } as NavigatorMessage

          const messages = _rootStore.messages.toJSON()
          const tripMessages = messages[tripId as string] || []
          const newMessages = [...tripMessages, navigatorMessage]

          _rootStore.setProp("messages", {
            ...messages,
            [tripId]: newMessages,
          })
        }

        if (payload.data.excludeUserIds) {
          const excludeUserIds = payload.data.excludeUserIds.split(",")
          if (excludeUserIds.includes(auth.currentUser?.uid)) {
            console.log("notification is ignored for current user", {
              notification,
              excludeUserIds,
            })
            return
          }
        }

        if (notification) displayNotification(notification)
      } catch (error) {
        console.error("fcm message handling failure", {
          error,
          payload,
        })
      }
    })
  })

  if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", serviceWorkerMessageListener)
  }

  return () => {
    clearTimeout(timeout)
    unsubscribeOnMessageEvent()
    if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener("message", serviceWorkerMessageListener)
    }
  }
}

async function handleNotificationClick(notificationTripId: string) {
  if (!_rootStore.activeTrip || _rootStore.activeTrip.id !== notificationTripId) {
    const notificationTrip = await getTrip(notificationTripId)
    if (notificationTrip) {
      _rootStore.setProp("cruiseDetailsPlaceholderData", null)
      _rootStore.setProp("activeTrip", notificationTrip)
    }
  }
}

const notificationsBucket = new Map<string, Notification>()

export function displayNotification(notification: INotificationPayload<Record<string, unknown>>) {
  const notificationTripId = notification.data?.tripId as string

  if (
    _rootStore.activeTrip?.id &&
    notificationTripId &&
    _rootStore.activeTrip?.id === notificationTripId
  ) {
    console.log("skip notification for active trip", {
      activeTripId: _rootStore.activeTrip?.id,
      notificationTripId,
      notification,
    })
    return
  }

  console.log("create notification", { notification })
  const webPushNotification = new Notification(notification.title, {
    body: notification.body,
    icon: "/logo.ico",
    data: notification,
  })

  webPushNotification.onshow = () => {
    console.log("show notification", {
      visibilityState: document.visibilityState,
      notification,
    })
  }

  webPushNotification.onclick = async () => {
    notificationsBucket.delete(notification.id)

    console.log("clicked notification", {
      notification,
    })
    // Focus the current tab if it's already open, or open a new one
    window.focus()
    // Alternatively, if you want to navigate to a specific page on your host, you can do:
    // window.location.href = window.location.origin; // This opens the current host's homepage.

    webPushNotification.close()

    await handleNotificationClick(notificationTripId)
  }

  webPushNotification.onclose = () => {
    console.log("close notification", {
      notification,
    })
  }

  notificationsBucket.set(notification.id, webPushNotification)

  incrementUnreadMessagesCount(notification)
}

export function cleanupNotifications() {
  const activeTripId = _rootStore.activeTrip?.id

  if (activeTripId) {
    notificationsBucket.forEach((notification) => {
      if (notification.data.tripId === activeTripId) {
        notification.close()
        notificationsBucket.delete(notification.data.id)
      }
    })
  }
}
